"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query"; // Validation
import { toast } from "sonner";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/api/api.ts"; // Validation schema

// Validation schema
const registrationSchema = z
  .object({
    firstName: z
      .string()
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must not exceed 50 characters"),

    lastName: z
      .string()
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must not exceed 50 characters"),

    email: z.string().email("Please enter a valid email address"),
    // .endsWith(".gov", "Email must be from a government domain
    // (.gov)"),

    phoneNumber: z
      .string()
      .regex(/^\+?[\d\s\-$$$$]+$/, "Please enter a valid phone number")
      .min(10, "Phone number must be at least 10 digits"),

    officialTitle: z
      .string()
      .min(2, "Official title is required")
      .max(100, "Official title must not exceed 100 characters"),

    password: z.string().min(5, "Password must be at least 8 characters"),

    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type RegistrationFormData = z.infer<typeof registrationSchema>;

export function GuestRegistration() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationKey: ["registerOfficials"],
    mutationFn: async (data: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
      phoneNumber: string;
      officialTitle: string;
    }) => {
      try {
        const response = await apiClient.post("/admin/registerOfficials", data);

        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onMutate: async (data: any) => {
      await queryClient.cancelQueries({
        queryKey: ["registerOfficials", "allOfficials"],
      });

      const previousSupervisors = queryClient.getQueryData<any>([
        "allOfficials",
      ]);

      queryClient.setQueryData<any>(["allOfficials"], (old: any) => {
        if (!old) return [];

        return [...old, { ...data }];
      });

      return { previousSupervisors };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allOfficials"] });
      toast.success("Government Official has been Registered successfully");
    },
    onError: useCallback(
      (error: unknown, context: any) => {
        if (error instanceof AxiosError) {
          toast.error(`${error.response?.data.message}`);
        }
        toast.error("Failed to register an Official. Reverting...");
        queryClient.setQueryData(
          ["allOfficials"],
          context?.previousSupervisors,
        );
      },
      [queryClient],
    ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allOfficials"] });
    },
  });

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, dirtyFields },
    // setValue,
    setError,
    // reset,
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      officialTitle: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async () => {
    try {
      const password = getValues("password");
      const confirmPassword = getValues("confirmPassword");

      if (password !== confirmPassword) {
        setError("confirmPassword", {
          type: "manual",
          message: "Passwords do not match",
        });

        return;
      }
      const formData = {
        firstName: getValues("firstName"),
        lastName: getValues("lastName"),
        email: getValues("email"),
        phoneNumber: getValues("phoneNumber"),
        officialTitle: getValues("officialTitle"),
        password: getValues("password"),
      };

      console.log("errors", errors);
      console.log("dirtyFields", dirtyFields);

      toast.promise(mutateAsync(formData), {
        loading: "Registering Government Official.",
        success: "SuccessFully Registered Government Official.",
        error: "Failed To Register a Government Official",
        position: "top-right",
      });

      // Reset success message after 5 seconds
    } catch (error) {
      console.error("Registration failed:", error);
    }
  };

  return (
    // <div className="h-full bg-gray-50 py-8 px-4">
    <div className="max-w-2xl mx-auto">
      <Card className="shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-900">
            Government Official Registration
          </CardTitle>
          <CardDescription className="text-gray-600">
            Please fill out all required fields to create your official account
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <form className="space-y-6" onSubmit={handleSubmit(() => onSubmit())}>
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Personal Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    {...register("firstName")}
                    className={errors.firstName ? "border-red-500" : ""}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-600">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    {...register("lastName")}
                    className={errors.lastName ? "border-red-500" : ""}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-600">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Government Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register("email")}
                    className={errors.email ? "border-red-500" : ""}
                    placeholder="your.name@agency.gov"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number *</Label>
                  <Input
                    id="phoneNumber"
                    {...register("phoneNumber")}
                    className={errors.phoneNumber ? "border-red-500" : ""}
                    placeholder="+1 (555) 123-4567"
                  />
                  {errors.phoneNumber && (
                    <p className="text-sm text-red-600">
                      {errors.phoneNumber.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Official Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Official Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="officialTitle">Official Title *</Label>
                  <Input
                    id="officialTitle"
                    {...register("officialTitle")}
                    className={errors.officialTitle ? "border-red-500" : ""}
                    placeholder="e.g., Senior Policy Analyst"
                  />
                  {errors.officialTitle && (
                    <p className="text-sm text-red-600">
                      {errors.officialTitle.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Security Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Security Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    {...register("password")}
                    className={errors.password ? "border-red-500" : ""}
                    placeholder="Create a secure password"
                  />
                  {errors.password && (
                    <p className="text-sm text-red-600">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    {...register("confirmPassword")}
                    className={errors.confirmPassword ? "border-red-500" : ""}
                    placeholder="Confirm your password"
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-600">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                type="submit"
              >
                Submit Registration
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
    // </div>
  );
}
